import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Card, Descriptions, Tag, Typography, Spin, Button, Tabs, Space, Divider
} from 'antd';
import { ArrowLeftOutlined, QrcodeOutlined, EditOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sheepApi } from '../api';
import { Sheep } from '../types';
import PedigreeTree from '../components/PedigreeTree';
import SheepForm from '../components/SheepForm';
import PhotoGallery from '../components/PhotoGallery';
import EventsJournal from '../components/EventsJournal';

const statusColors: Record<string, string> = { alive: 'green', for_sale: 'orange', sold: 'blue', deceased: 'red' };
const statusLabels: Record<string, string> = { alive: 'Живой', for_sale: 'В продаже', sold: 'Продан', deceased: 'Погиб' };

function DI({ label, value, children }: { label: string; value?: any; children?: React.ReactNode }) {
  return (
    <Descriptions.Item label={label}>
      {children ?? (value != null && value !== '' ? value : '—')}
    </Descriptions.Item>
  );
}

export default function SheepDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sheep, setSheep] = useState<Sheep | null>(null);
  const [loading, setLoading] = useState(true);
  const [qr, setQr] = useState('');
  const [editing, setEditing] = useState(false);
  const [qrVisible, setQrVisible] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const sr = await sheepApi.get(Number(id));
      setSheep(sr.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const loadQR = async () => {
    if (!id) return;
    if (!qr) {
      const r = await sheepApi.qr(Number(id));
      setQr(r.data.qr);
    }
    setQrVisible((v) => !v);
  };

  if (loading) return <Spin style={{ display: 'block', marginTop: 100 }} size="large" />;
  if (!sheep) return <Typography.Text>Баран не найден</Typography.Text>;

  const ageText = sheep.age ? `${sheep.age.years} лет ${sheep.age.months} мес.` : '—';
  const children = [...(sheep.childrenAsFather || []), ...(sheep.childrenAsMother || [])];

  if (editing) {
    return (
      <Card title="Редактировать барана" extra={
        <Button icon={<ArrowLeftOutlined />} onClick={() => setEditing(false)}>Назад</Button>
      }>
        <SheepForm
          initial={sheep}
          onSuccess={() => { setEditing(false); load(); }}
          onCancel={() => setEditing(false)}
        />
      </Card>
    );
  }

  const isSale = sheep.status === 'for_sale' || sheep.status === 'sold';
  const canEdit = user?.role === 'admin' || sheep.ownerId === user?.id;

  return (
    <div>
      {/* ── Кнопки действий ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1 as any)}>
          Назад
        </Button>
        {canEdit && (
          <Button icon={<EditOutlined />} type="primary" onClick={() => setEditing(true)}>
            Редактировать
          </Button>
        )}
        <Button icon={<QrcodeOutlined />} onClick={loadQR}>
          {qrVisible ? 'Скрыть QR' : 'QR-код'}
        </Button>
      </div>

      {/* ── QR-код (раскрывается под кнопками) ── */}
      {qrVisible && qr && (
        <Card size="small" style={{ marginBottom: 12, textAlign: 'center', maxWidth: 280 }}>
          <img src={qr} alt="QR" style={{ width: '100%', maxWidth: 220 }} />
          <Button size="small" type="primary" style={{ marginTop: 8, width: '100%' }}
            onClick={() => {
              const w = window.open('');
              w?.document.write(`<html><body style="text-align:center"><img src="${qr}" width="300"/><p>${sheep.name} — ${sheep.tagNumber}</p></body></html>`);
              w?.print();
            }}>
            Распечатать
          </Button>
        </Card>
      )}

      {/* ── Заголовок карточки ── */}
      <Card size="small" style={{ marginBottom: 12, borderRadius: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <span style={{ fontSize: 28 }}>{sheep.gender === 'male' ? '♂' : '♀'}</span>
          <Typography.Title level={4} style={{ margin: 0 }}>{sheep.name}</Typography.Title>
          <Tag color="purple" style={{ margin: 0 }}>{sheep.tagNumber}</Tag>
          <Tag color={statusColors[sheep.status]} style={{ margin: 0 }}>{statusLabels[sheep.status]}</Tag>
        </div>

        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Дата рождения">
            {new Date(sheep.birthDate).toLocaleDateString('ru-RU')}
          </Descriptions.Item>
          <Descriptions.Item label="Возраст">{ageText}</Descriptions.Item>
          <Descriptions.Item label="Пол">
            {sheep.gender === 'male' ? 'Самец (баран)' : 'Самка (овца)'}
          </Descriptions.Item>
          <DI label="Порода" value={sheep.breed} />
          <DI label="Вес" value={sheep.weight} children={sheep.weight ? `${sheep.weight} кг` : undefined} />
          <DI label="Линия / кровь" value={sheep.bloodline} />
          {isSale && sheep.salePrice && (
            <Descriptions.Item label="Цена продажи">
              <b style={{ color: '#389e0d' }}>{Number(sheep.salePrice).toLocaleString('ru-RU')} сом</b>
            </Descriptions.Item>
          )}

          <DI label="Длина ушей" value={sheep.earLength} children={sheep.earLength ? `${sheep.earLength} см` : undefined} />
          <DI label="Рост в холке" value={sheep.withersHeight} children={sheep.withersHeight ? `${sheep.withersHeight} см` : undefined} />
          <DI label="Костяк" value={sheep.boneStructure} children={sheep.boneStructure ? `${sheep.boneStructure} см` : undefined} />
          <DI label="Обхват груди" value={sheep.chestGirth} children={sheep.chestGirth ? `${sheep.chestGirth} см` : undefined} />
          <DI label="Обхват курдюка" value={sheep.fattailGirth} children={sheep.fattailGirth ? `${sheep.fattailGirth} см` : undefined} />
          <DI label="Длина" value={sheep.bodyLength} children={sheep.bodyLength ? `${sheep.bodyLength} см` : undefined} />

          {/* Родители */}
          {sheep.father && (
            <Descriptions.Item label="Отец">
              <Button type="link" style={{ padding: 0 }}
                onClick={() => navigate(`/sheep/${sheep.father!.id}/detail`)}>
                {sheep.father.name} ({sheep.father.tagNumber})
              </Button>
            </Descriptions.Item>
          )}
          {sheep.mother && (
            <Descriptions.Item label="Мать">
              <Button type="link" style={{ padding: 0 }}
                onClick={() => navigate(`/sheep/${sheep.mother!.id}/detail`)}>
                {sheep.mother.name} ({sheep.mother.tagNumber})
              </Button>
            </Descriptions.Item>
          )}

          {/* Беременность */}
          {sheep.gender === 'female' && sheep.lambingNumber && (
            <Descriptions.Item label="Окот по счёту">
              <Tag color="purple">{sheep.lambingNumber}-й окот</Tag>
            </Descriptions.Item>
          )}
          {sheep.gender === 'female' && sheep.conceptionDate && (() => {
            const from = dayjs(sheep.conceptionDate).add(147, 'day').format('DD.MM.YYYY');
            const to   = dayjs(sheep.conceptionDate).add(150, 'day').format('DD.MM.YYYY');
            return (
              <>
                <Descriptions.Item label="Дата зачатия">
                  {new Date(sheep.conceptionDate).toLocaleDateString('ru-RU')}
                </Descriptions.Item>
                <Descriptions.Item label="Примерная дата родов">
                  <Tag color="green">🐣 {from} — {to}</Tag>
                </Descriptions.Item>
              </>
            );
          })()}

          {sheep.notes && (
            <Descriptions.Item label="Заметки">{sheep.notes}</Descriptions.Item>
          )}
        </Descriptions>

        {children.length > 0 && (
          <>
            <Divider style={{ margin: '12px 0' }}>Потомство ({children.length})</Divider>
            <Space wrap>
              {children.map((c) => (
                <Tag key={c.id} color="geekblue" style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sheep/${c.id}/detail`)}>
                  {c.gender === 'male' ? '♂' : '♀'} {c.name} ({c.tagNumber})
                </Tag>
              ))}
            </Space>
          </>
        )}
      </Card>

      {/* ── Фото ── */}
      <Card size="small" title="Фото" style={{ marginBottom: 12, borderRadius: 12 }}>
        <PhotoGallery sheepId={sheep.id} readOnly={!canEdit} />
      </Card>

      {/* ── Вкладки ── */}
      <Card size="small" style={{ borderRadius: 12 }}>
        <Tabs items={[
          {
            key: 'pedigree',
            label: '🌳 Родословная',
            children: (
              <div>
                <div style={{ textAlign: 'right', marginBottom: 12 }}>
                  <Button onClick={() => navigate(`/sheep/${sheep.id}/full-pedigree`)}>
                    🌳 Полная родословная
                  </Button>
                </div>
                <div style={{ overflowX: 'auto', padding: 4 }}>
                  <PedigreeTree sheep={sheep} />
                </div>
              </div>
            ),
          },
          {
            key: 'journal',
            label: '📋 Журнал событий',
            children: <EventsJournal sheepId={sheep.id} readOnly={!canEdit} />,
          },
        ]} />
      </Card>
    </div>
  );
}
